import CompanySearch from '@/components/e_invoicing/CompanySearch'
import ElectronicAddressPicker from '@/components/e_invoicing/ElectronicAddressPicker'
import { CompanySearchResult, ContractFormValues, SharedProps, Zone } from '@/types'
import { Button, Checkbox, Divider, Group, InputBase, NumberInput, Select, Stack, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { usePage } from '@inertiajs/react'
import { IMaskInput } from 'react-imask'
import { ChangeEvent } from 'react'

export default function ContractForm({
  onSubmit,
  initialValues,
  submitLabel = 'Créer le contrat',
  showSmsNotification,
  zones,
}: {
  onSubmit: (contract: ContractFormValues) => void
  initialValues?: Partial<ContractFormValues>
  submitLabel?: string
  showSmsNotification: boolean
  zones: Zone[]
}) {
  const { current_organization } = usePage<SharedProps>().props
  const organizationDefault = current_organization?.default_days_to_deliver ?? 7

  const form = useForm<ContractFormValues>({
    initialValues: {
      customer_name: initialValues?.customer_name ?? '',
      customer_address: initialValues?.customer_address ?? '',
      customer_phone_number: initialValues?.customer_phone_number ?? '',
      customer_email: initialValues?.customer_email ?? '',
      collected_by_provider: initialValues?.collected_by_provider ?? false,
      sms_enabled: initialValues?.sms_enabled ?? false,
      default_days_to_deliver: initialValues?.default_days_to_deliver ?? null,
      zone_id: initialValues?.zone_id ?? null,
      legal_name: initialValues?.legal_name ?? '',
      siren: initialValues?.siren ?? '',
      siret: initialValues?.siret ?? '',
      vat_number: initialValues?.vat_number ?? '',
      street_address: initialValues?.street_address ?? '',
      postal_code: initialValues?.postal_code ?? '',
      city: initialValues?.city ?? '',
      electronic_address: initialValues?.electronic_address ?? '',
    },
  })

  const handleCompanySelect = (company: CompanySearchResult) =>
    form.setValues({
      customer_name: form.values.customer_name || company.legal_name,
      legal_name: company.legal_name,
      siren: company.siren,
      siret: company.siret ?? '',
      vat_number: company.vat_number ?? '',
      street_address: company.street_address ?? '',
      postal_code: company.postal_code ?? '',
      city: company.city ?? '',
      electronic_address: company.siren === form.values.siren ? form.values.electronic_address : '',
    })

  const handleElectronicAddressSelect = (identifier: string) => form.setFieldValue('electronic_address', identifier)

  const zoneOptions = zones.map((zone) => ({ value: String(zone.id), label: zone.name }))
  const handleZoneChange = (value: string | null) => form.setFieldValue('zone_id', value ? Number(value) : null)

  const handleCollectedByProviderChange = (event: ChangeEvent<HTMLInputElement>) => {
    const collectedByProvider = event.currentTarget.checked
    form.setFieldValue('collected_by_provider', collectedByProvider)
    if (!collectedByProvider) form.setFieldValue('zone_id', null)
  }

  const handleSubmit = (values: ContractFormValues) =>
    onSubmit({
      ...values,
      zone_id: values.collected_by_provider ? values.zone_id : null,
      default_days_to_deliver:
        values.default_days_to_deliver === null || (values.default_days_to_deliver as unknown as string) === ''
          ? null
          : Number(values.default_days_to_deliver),
    })

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Nom de la société"
          placeholder="Ex: Lacoste SARL"
          {...form.getInputProps('customer_name')}
          required
          data-autofocus
        />

        <Group grow align="flex-start">
          <InputBase
            component={IMaskInput}
            mask="00 00 00 00 00"
            label="Téléphone"
            placeholder="01 00 00 00 00"
            {...form.getInputProps('customer_phone_number')}
          />
          <TextInput label="Email" placeholder="contact@societe.com" {...form.getInputProps('customer_email')} />
        </Group>

        <TextInput
          label="Adresse de l'établissement"
          description="Lieu de collecte et de livraison des commandes."
          placeholder="123 rue de la Paix"
          {...form.getInputProps('customer_address')}
        />

        <NumberInput
          label="Délai de livraison"
          description="En jours calendaires. Laisser vide pour utiliser le délai par défaut de l'organisation."
          placeholder={String(organizationDefault)}
          min={1}
          allowDecimal={false}
          {...form.getInputProps('default_days_to_deliver')}
        />

        <Checkbox
          label="Le prestataire collecte les commandes sur place"
          checked={form.values.collected_by_provider}
          onChange={handleCollectedByProviderChange}
        />

        {zones.length > 0 && form.values.collected_by_provider && (
          <Select
            label="Zone"
            description="Détermine les jours de tournée du coursier pour cette boutique."
            placeholder="Aucune zone"
            data={zoneOptions}
            value={form.values.zone_id ? String(form.values.zone_id) : null}
            onChange={handleZoneChange}
            clearable
          />
        )}

        <Divider label="Facturation" labelPosition="left" mt="sm" />

        <CompanySearch onSelect={handleCompanySelect} />

        <Group grow align="flex-start">
          <TextInput label="Raison sociale" {...form.getInputProps('legal_name')} />
          <TextInput label="SIREN" placeholder="9 chiffres" {...form.getInputProps('siren')} />
        </Group>

        <Group grow align="flex-start">
          <TextInput label="SIRET de facturation" {...form.getInputProps('siret')} />
          <TextInput
            label="N° de TVA intracommunautaire"
            description="Laisser vide si la société n'en a pas."
            {...form.getInputProps('vat_number')}
          />
        </Group>

        <TextInput label="Adresse de facturation" {...form.getInputProps('street_address')} />

        <Group grow align="flex-start">
          <TextInput label="Code postal" {...form.getInputProps('postal_code')} />
          <TextInput label="Ville" {...form.getInputProps('city')} />
        </Group>

        <TextInput
          label="Adresse de facturation électronique"
          description="Où la société reçoit ses factures électroniques, par exemple 0225:123456789."
          placeholder="0225:123456789"
          {...form.getInputProps('electronic_address')}
        />

        {current_organization?.e_invoicing_available && (
          <ElectronicAddressPicker
            siren={form.values.siren ?? ''}
            value={form.values.electronic_address ?? ''}
            onSelect={handleElectronicAddressSelect}
          />
        )}

        {showSmsNotification && (
          <Checkbox
            label="Notifier la société par SMS quand la commande est prête"
            description="Uniquement si le numéro de téléphone renseigné est un mobile."
            {...form.getInputProps('sms_enabled', { type: 'checkbox' })}
          />
        )}

        <Button type="submit" radius="md" fullWidth size="md">
          {submitLabel}
        </Button>
      </Stack>
    </form>
  )
}
